import { useMemo } from "react";
import type { CompanyFilters } from "@/lib/company-filter";
import { stripIntentUiFields } from "@/lib/company-filter/utils/stripIntentUiFields";
import {
  buildModelScoreFilter,
  withModelScoreFilterModels,
} from "@/lib/company-filter/utils/buildModelScoreFilter";
import { buildModelScores } from "@/lib/scoring/scoringUtils";
import type { ScoringModel } from "@/apis/argo/scoringModels";
import {
  ModelScoreFilterColumn,
  type ModelScoreFilterInput,
  type ModelScoreInput,
  type OpportunityGeneratorFilter,
} from "@/generated/company-search";
import { V2_TIERS } from "./constants";

/** Every V2 tier except DQ — an account the model couldn't grade shouldn't
 * silently pad Market Sizing's headline once a model is active. Selecting
 * this for the model whenever the user hasn't chosen tiers themselves keeps
 * the headline "baseline" but graded-only, instead of grade-blind. */
const NON_DQ_TIERS = V2_TIERS.filter((tier) => tier !== "DQ");

interface UseMarketSizingQueryArgsParams {
  appliedFilters: CompanyFilters | undefined;
  urlSearch: string;
  scoringV2Enabled: boolean;
  activeModelFamilies: string[];
  models: ScoringModel[];
}

interface UseMarketSizingQueryArgsResult {
  matrixBaseFilters: OpportunityGeneratorFilter | undefined;
  marketSizingFilters: OpportunityGeneratorFilter | undefined;
  matrixQueryModels: ScoringModel[];
  matrixModelScoreFilter: ModelScoreFilterInput[] | undefined;
  marketSizingModelScoreFilter: ModelScoreFilterInput[] | undefined;
  matrixModelScores: ModelScoreInput[];
}

/** The company-search query args shared by the scoring matrix and Market
 * Sizing — moved verbatim out of CompaniesExplorePage so the share page (which
 * has no matrix) can build the same args from its own inputs. */
export function useMarketSizingQueryArgs({
  appliedFilters,
  urlSearch,
  scoringV2Enabled,
  activeModelFamilies,
  models,
}: UseMarketSizingQueryArgsParams): UseMarketSizingQueryArgsResult {
  // Segment filters shared by the matrix total count and the per-cell queries.
  const matrixBaseFilters = useMemo(
    () => stripIntentUiFields(appliedFilters),
    [appliedFilters]
  );
  // Market Sizing's own filters, with the search box folded in the same way
  // CompaniesExploreList does it internally — without this, typing a search
  // narrows the Company List tab while the Market Sizing headline/charts
  // keep sizing the unsearched segment.
  const marketSizingFilters = useMemo(() => {
    const trimmedSearch = urlSearch.trim();
    return trimmedSearch
      ? { ...matrixBaseFilters, fuzzySearch: trimmedSearch }
      : matrixBaseFilters;
  }, [matrixBaseFilters, urlSearch]);
  // Same V2 model-score filter the list applies, so the workbook cap gate counts
  // the filtered set — not the whole (unfiltered) segment.
  const activeModels = useMemo(
    () =>
      scoringV2Enabled
        ? activeModelFamilies
            .map((family) => models.find((m) => m.family === family))
            .filter((m): m is NonNullable<typeof m> => m !== undefined)
        : [],
    [scoringV2Enabled, activeModelFamilies, models]
  );
  // Displayed models plus any model a stored filter targets — hiding a model
  // must not unfilter the counts (RGI-2347). Must match CompaniesExploreList.
  const matrixQueryModels = useMemo(
    () =>
      withModelScoreFilterModels(
        scoringV2Enabled,
        activeModels,
        models,
        appliedFilters
      ),
    [scoringV2Enabled, activeModels, models, appliedFilters]
  );
  const matrixModelScoreFilter = useMemo(
    () =>
      buildModelScoreFilter(
        scoringV2Enabled,
        matrixQueryModels,
        appliedFilters
      ),
    [scoringV2Enabled, matrixQueryModels, appliedFilters]
  );
  // Market Sizing's own headline needs one more thing than the shared
  // matrixModelScoreFilter above: for any ACTIVE (displayed) V2 model the
  // user hasn't explicitly set a tier filter for yet, default that model to
  // "every tier but DQ" — so the headline excludes not-yet-graded/disqualified
  // spend automatically, the same way the MVP's own headline always has,
  // rather than waiting for a manual tier click. A model the user HAS set
  // tiers for (here or via the Add Filter modal's Model Score control — same
  // shared modelScoreFilterUi entries either way) is left exactly as
  // matrixModelScoreFilter already built it; this only fills the gap where
  // that array is silent about an active model.
  const marketSizingModelScoreFilter = useMemo(() => {
    if (!scoringV2Enabled || activeModels.length === 0) {
      return matrixModelScoreFilter;
    }
    const explicitEntries = appliedFilters?.modelScoreFilterUi?.entries ?? [];
    const modelsWithExplicitTiers = new Set(
      explicitEntries
        .filter((entry) => entry.tiers && entry.tiers.length > 0)
        .map((entry) => entry.modelId)
    );
    const defaults: ModelScoreFilterInput[] = activeModels
      .filter((model) => !modelsWithExplicitTiers.has(model.family))
      .map((model) => ({
        modelId: model.family,
        column: ModelScoreFilterColumn.TIER,
        in: NON_DQ_TIERS,
      }));
    return defaults.length > 0
      ? [...(matrixModelScoreFilter ?? []), ...defaults]
      : matrixModelScoreFilter;
  }, [scoringV2Enabled, activeModels, appliedFilters, matrixModelScoreFilter]);
  // Model scores + tenant that the query needs to resolve the filter. Only
  // relevant when V2 models are carried.
  const matrixModelScores = useMemo(
    () => buildModelScores(matrixQueryModels),
    [matrixQueryModels]
  );

  return {
    matrixBaseFilters,
    marketSizingFilters,
    matrixQueryModels,
    matrixModelScoreFilter,
    marketSizingModelScoreFilter,
    matrixModelScores,
  };
}
